/**
 * Pure Talk zone model. Derives the ordered zone list the Talk picker spans —
 * Home, each saved zone (canonical order), then the transient live "Nearby" ring —
 * from the same inputs as buildMonitoredRings (monitored-places.ts). `name` is the
 * zone's own placeName or "" (the caller substitutes an i18n fallback per kind).
 * A live zone with cell "" (GPS not yet acquired) is still listed so its chip shows.
 */
import { cellToParent } from "h3-js";
import { AREA_RES } from "./talk-geo.js";

export type TalkZoneKind = "home" | "saved" | "live";

export interface TalkZone {
  id: string;
  name: string;
  cell: string;
  radiusKm: number;
  kind: TalkZoneKind;
}

export function talkZones(
  home: { cell: string; radiusKm: number; placeName?: string } | undefined,
  places: ReadonlyArray<{ id: string; cell: string; radiusKm: number; placeName?: string }>,
  live: { cell: string; radiusKm: number } | null,
): TalkZone[] {
  const zones: TalkZone[] = [];
  if (home?.cell) {
    zones.push({ id: "home", name: home.placeName ?? "", cell: home.cell, radiusKm: home.radiusKm, kind: "home" });
  }
  for (const p of places) {
    zones.push({ id: p.id, name: p.placeName ?? "", cell: p.cell, radiusKm: p.radiusKm, kind: "saved" });
  }
  if (live) {
    zones.push({ id: "live", name: "", cell: live.cell, radiusKm: live.radiusKm, kind: "live" });
  }
  return zones;
}

/** Res-6 footprint of a cell — used to decide when a moving live zone has shifted
 *  enough to warrant re-subscribing its area (avoids per-GPS-tick churn). */
export function liveAreaFootprint(cell: string): string {
  return cell === "" ? "" : cellToParent(cell, AREA_RES);
}

/** Hold a stable live-zone cell: adopt `current` only when its res-6 footprint
 *  differs from the held one, so the live zone's area cells (and thus its
 *  subscription) change only when the user has moved a neighbourhood. */
export function snapLiveCell(held: string, current: string): string {
  if (current === "") return held;
  return liveAreaFootprint(held) === liveAreaFootprint(current) ? held : current;
}
